var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faBars, faCog, faDatabase, faSave, faTable, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import axios from "axios";
import { BAlert, BButton, BSpinner, BTab, BTabs } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { copyCollection } from "@/api/datasetCollections";
import { updateContentFields } from "@/components/History/model/queries";
import { DatatypesProvider, DbKeyProvider, SuitableConvertersProvider } from "@/components/providers";
import { useConfig } from "@/composables/config";
import { useCollectionAttributesStore } from "@/stores/collectionAttributesStore";
import { useCollectionElementsStore } from "@/stores/collectionElementsStore";
import { useHistoryStore } from "@/stores/historyStore";
import localize from "@/utils/localization";
import { prependPath } from "@/utils/redirect";
import { errorMessageAsString } from "@/utils/simple-error";
import { updateHistoryItemsBulk } from "./services";
import ChangeDatatypeTab from "@/components/Collections/common/ChangeDatatypeTab.vue";
import DatabaseEditTab from "@/components/Collections/common/DatabaseEditTab.vue";
import SuitableConvertersTab from "@/components/Collections/common/SuitableConvertersTab.vue";
import Heading from "@/components/Common/Heading.vue";
import FormDisplay from "@/components/Form/FormDisplay.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'CollectionEditView',
    props: {
        collectionId: null
    },
    setup(__props) {
        const props = __props;
        library.add(faBars, faCog, faDatabase, faSave, faTable, faUser);
        const { config, isConfigLoaded } = useConfig(true);
        const collectionAttributesStore = useCollectionAttributesStore();
        const historyStore = useHistoryStore();
        const { currentHistoryId } = storeToRefs(historyStore);
        const collectionStore = useCollectionElementsStore();
        const jobError = ref(null);
        const errorMessage = ref("");
        const infoMessage = ref("");
        const successMessage = ref("");
        const attributesInputs = ref([]);
        /** Used to track if there has been a change in the collection, and rerenders `FormDisplay` */
        const collectionChangeKey = ref(0);
        const attributesData = computed(() => {
            return collectionAttributesStore.getAttributes(props.collectionId);
        });
        const attributesLoadError = computed(() => {
            const itemLoadError = collectionAttributesStore.getItemLoadError(props.collectionId);
            if (itemLoadError) {
                return errorMessageAsString(itemLoadError);
            }
            return undefined;
        });
        const collection = computed(() => {
            return collectionStore.getCollectionById(props.collectionId);
        });
        const collectionLoadError = computed(() => {
            if (collection.value) {
                const collectionElementLoadError = collectionStore.getLoadingCollectionElementsError(collection.value);
                if (collectionElementLoadError) {
                    return errorMessageAsString(collectionElementLoadError);
                }
            }
            return undefined;
        });
        watch([attributesLoadError, collectionLoadError], () => {
            if (attributesLoadError.value) {
                errorMessage.value = attributesLoadError.value;
            }
            else if (collectionLoadError.value) {
                errorMessage.value = collectionLoadError.value;
            }
        });
        const databaseKeyFromElements = computed(() => {
            var _a;
            return (_a = attributesData.value) === null || _a === void 0 ? void 0 : _a.dbkey;
        });
        const datatypeFromElements = computed(() => {
            var _a;
            return (_a = attributesData.value) === null || _a === void 0 ? void 0 : _a.extension;
        });
        watch(() => collection.value, (newVal) => {
            if (newVal) {
                collectionChangeKey.value++;
                attributesInputs.value = [
                    {
                        name: "name",
                        label: "Name",
                        type: "text",
                        value: newVal.name,
                    },
                ];
            }
        }, { immediate: true });
        function updateInfoMessage(strMessage) {
            infoMessage.value = strMessage;
            successMessage.value = "";
        }
        // TODO: Replace with actual datatype type
        function clickedSave(attribute, newValue) {
            return __awaiter(this, void 0, void 0, function* () {
                if (attribute !== "dbkey") {
                    // TODO: extend this to other attributes that could be changed
                    console.error(`Changing ${attribute} not implemented`);
                    return;
                }
                const dbKey = newValue.id;
                try {
                    yield copyCollection(props.collectionId, dbKey);
                }
                catch (err) {
                    errorMessage.value = errorMessageAsString(err, `Changing ${attribute} failed`);
                }
            });
        }
        // TODO: Replace with actual datatype type
        function clickedConvert(selectedConverter) {
            return __awaiter(this, void 0, void 0, function* () {
                const url = prependPath(`/api/tools/${selectedConverter.tool_id}/convert`);
                const data = {
                    src: "hdca",
                    id: props.collectionId,
                    source_type: selectedConverter.original_type,
                    target_type: selectedConverter.target_type,
                };
                try {
                    yield axios.post(url, data).catch(handleError);
                    successMessage.value = "Conversion started successfully.";
                }
                catch (err) {
                    errorMessage.value = errorMessageAsString(err, "Conversion failed.");
                }
            });
        }
        // TODO: Replace with actual datatype type
        function clickedDatatypeChange(selectedDatatype) {
            var _a;
            return __awaiter(this, void 0, void 0, function* () {
                const data = {
                    items: [
                        {
                            history_content_type: "dataset_collection",
                            id: props.collectionId,
                        },
                    ],
                    operation: "change_datatype",
                    params: {
                        type: "change_datatype",
                        datatype: selectedDatatype.id,
                    },
                };
                try {
                    yield updateHistoryItemsBulk((_a = currentHistoryId.value) !== null && _a !== void 0 ? _a : "", data);
                    successMessage.value = "Datatype changed successfully.";
                }
                catch (err) {
                    errorMessage.value = errorMessageAsString(err, "Datatype change failed.");
                }
            });
        }
        function handleError(err) {
            var _a;
            errorMessage.value = errorMessageAsString(err, "Datatype conversion failed.");
            if ((_a = err === null || err === void 0 ? void 0 : err.data) === null || _a === void 0 ? void 0 : _a.stderr) {
                jobError.value = err.data;
            }
        }
        function onAttribute(data) {
            var _a;
            for (const key in data) {
                const index = (_a = attributesInputs.value) === null || _a === void 0 ? void 0 : _a.findIndex((input) => input.name === key);
                if (index !== -1 && attributesInputs.value[index]) {
                    attributesInputs.value[index].value = data[key];
                }
            }
        }
        function saveAttrs() {
            return __awaiter(this, void 0, void 0, function* () {
                if (collection.value && attributesInputs.value) {
                    const updatedAttrs = attributesInputs.value.reduce((acc, input) => {
                        acc[input.name] = input.value;
                        return acc;
                    }, {});
                    try {
                        yield updateContentFields(collection.value, updatedAttrs);
                        successMessage.value = "Attributes updated successfully.";
                    }
                    catch (err) {
                        errorMessage.value = errorMessageAsString(err, "Unable to update attributes.");
                    }
                }
            });
        }
        return { __sfc: true, props, config, isConfigLoaded, collectionAttributesStore, historyStore, currentHistoryId, collectionStore, jobError, errorMessage, infoMessage, successMessage, attributesInputs, collectionChangeKey, attributesData, attributesLoadError, collection, collectionLoadError, databaseKeyFromElements, datatypeFromElements, updateInfoMessage, clickedSave, clickedConvert, clickedDatatypeChange, handleError, onAttribute, saveAttrs, faBars, faCog, faDatabase, faSave, faTable, FontAwesomeIcon, BAlert, BButton, BSpinner, BTab, BTabs, DatatypesProvider, DbKeyProvider, SuitableConvertersProvider, localize, ChangeDatatypeTab, DatabaseEditTab, SuitableConvertersTab, Heading, FormDisplay, LoadingSpan };
    }
});
